import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaArrowUp, FaMicrophoneAlt } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import logo from "../../images/LogoOnlyAnimation.gif";
import jooLogo from "../../images/joominLogo.png";
import jooIcon from "../../images/JOOMINEYE.png";
import axios from "axios";
import { useQuestion } from '../../context/QuestionContext';
import { useNavigate } from "react-router-dom";
import { sendMessage } from '../../api/chatApi';
import { generateRandomId } from '../../utils/helper';
import { getSessionId, saveSessionId } from "../../utils/storage";

function Chat() {
  const { sharedQuestion } = useQuestion();
  const [selectedImageName, setSelectedImageName] = useState(null); // New state for image name
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [input, setInput] = useState(sharedQuestion || "");
  const [response, setResponse] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDots, setLoadingDots] = useState("");

  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const initialRenderRef = useRef(true);

  const navigate = useNavigate();

  useEffect(() => {
    window.onload = () => {
      const currentSessionId = getSessionId();
      const currentPath = window.location.pathname;

      if (currentSessionId && currentPath === "/send") {
        navigate(`/history/${currentSessionId}`);
      }
    };

    let body = document.body;
    body.style.backgroundColor = "#2D111F";

  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingDots((prevDots) =>
        prevDots.length >= 4 ? "" : prevDots + "."
      );
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (initialRenderRef.current && sharedQuestion) {
      handleSubmit();
      initialRenderRef.current = false;
    }
  }, [sharedQuestion]);

  useEffect(() => {
    // Scroll to the bottom whenever messages change
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [response]);

  const handleKeyDown = (event) => {
    if (event && event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (input.trim() === "") return;

    const sessionId = generateRandomId(32);
    saveSessionId(sessionId);

    setResponse((prevResponses) => [
      ...prevResponses,
      { question: input, answer: "", isLoading: true },
    ]);
    setIsLoading(true);
    setInput("");

    try {
      const result = await sendMessage({
        message: input,
        session_id: sessionId
      });

      const answer = result.response;
      const images = result.images;

      let i = 0;
      const speed = 15;
      let tempAnswer = "";

      const typeWriter = () => {
        if (i < answer.length) {
          tempAnswer += answer.charAt(i);
          setResponse((prevResponses) => {
            const updatedResponses = [...prevResponses];
            updatedResponses[updatedResponses.length - 1] = {
              ...updatedResponses[updatedResponses.length - 1],
              answer: tempAnswer,
              images,
              isLoading: false,
            };
            return updatedResponses;
          });
          i++;
          setTimeout(typeWriter, speed);
        } else {
          setIsLoading(false);
        }
      };

      setTimeout(typeWriter, 500); // Start typing effect after a short delay

      setInput("");
    } catch (error) {
      setResponse((prev) => [
        ...prev,
        {
          question: input,
          answer: "An error occurred while fetching the answer.",
        },
      ]);

      setInput("");
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="w-full flex justify-center h-[80vh]"
    >
      <div className="w-5/6 lg:w-52 h-full">
        <div className="flex justify-center items-center gap-2">
          <img src={logo} className="w-28 md:w-32" alt="Gif" />
          <div className="h-12 w-1 bg-white mr-4"></div>
          <img src={jooLogo} alt="" />
        </div>
        <div
          className="mb-4 overflow-y-auto   h-[45vh] md:h-[55vh] text-white "
          style={{ scrollbarWidth: "none" }}
        >
          {response.map((message, index) => (
            <div key={index} className="my-4">
              {message.question && (
                <div className="p-4 bg-gray-400/15 rounded-lg w-fit max-w-full ml-auto text-white ">
                  {message.question}
                </div>
              )}
              <div className="p-4 bg-purpleColor/10 my-4 w-fit rounded-lg text-white max-w-full ">
                <div className="flex gap-2 lg:gap-3">
                  <img
                    className="w-fit h-fit object-contain "
                    src={jooIcon}
                    alt=""
                  />
                  {message.isLoading ? (
                    <div className="flex items-center space-x-1">
                      <span className="">is thinking</span>
                      <span className="w-8 text-left">{loadingDots}</span>
                    </div>
                  ) : (
                    <div className="">
                      {message.images && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {message.images.map((image, index) => (
                            <button
                              key={index}
                              className="h-fit w-fit rounded-lg border-4  border-greenColorLight block bg-white p-4"
                              onClick={() => {
                                setSelectedImageName(image); // Set the selected image
                                toggleModal();
                              }}
                            >
                              <img
                                className="h-full w-full"
                                src={image}
                                alt="Received"
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {message.answer && (
                        <div
                          className="mt-4"
                          id="demo"
                          dangerouslySetInnerHTML={{ __html: message.answer }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isModalOpen && (
                <div className="fixed inset-0 z-20 bg-black/60">
                  <div className="relative flex items-center justify-center w-full h-full">
                    <div className="text-center">
                      <img
                        className="h-auto lg:h-[43rem] max-w-full bg-white p-6"
                        src={selectedImageName}
                        alt="toys"
                      />
                    </div>
                    <button
                      className="absolute top-4 right-4 p-1 text-3xl bg-red-500 text-white rounded-lg"
                      onClick={toggleModal}
                    >
                      <IoMdClose />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div ref={bottomRef} />
        </div>

        <div className="relative ">
          <textarea
            ref={textareaRef}
            name="chat"
            id="chat"
            className="w-full my-4 py-4 pl-3 rounded-lg h-14 max-h-52 resize-none bg-white bg-opacity-40 text-black  placeholder-pinkColor pr-[10rem]"
            type="text"
            placeholder="Ask JOOMINEYE"
            onKeyDown={handleKeyDown}
            value={input}
            disabled={isLoading}
            onChange={(e) => setInput(e.target.value)}
            style={{ scrollbarWidth: "none" }}
          ></textarea>

          <div className="flex items-center gap-2 absolute right-4 bottom-8">
            <span className="text-lightPurple text-4xl">
              <FaMicrophoneAlt />
            </span>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="h-8 w-8 rounded-full bg-gradient-to-r from-lightPurple to-lightPurple2 text-black flex justify-center items-center"
            >
              <FaArrowUp />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default Chat;
